import { map, Observable } from "rxjs";
import { GetScenariaResponse } from "../request-response/get-scenaria-response";
import AxiosService from "./axios-service";
import { ScenarioModel } from "../models/scenario-model";
import { PollutantModel } from "../models/pollutant-model";
import { AnalysisModel } from "../models/analysis-model";
import { AxiosResponse } from "axios";
import { AnalysisDataReceptorModel } from "../models/analysis-data-receptor-model";
import { AnalysisDataReceptorDateConcentrationModel } from "../models/analysis-data-receptor-date-concentration-model";
import dayjs from "dayjs";
import { AnalysisContourLinesModel } from "../models/analysis-countour-lines-model";
import { AnalysisContourLinesDailyModel } from "../models/analysis-countour-lines-daily-model";

class ScenariaService {    
    private static instance: ScenariaService | null = null;

    private constructor() {
    }

    public static getInstance(): ScenariaService {
        if (this.instance === null) {
            this.instance = new ScenariaService();
        }
        return this.instance;
    }

    getScenaria(page:number, pageSize:number):Observable<GetScenariaResponse> {
        return AxiosService.getInstance().get(`/api/scenaria?page=${page}&per_page=${pageSize}`);
    }

    getScenario(id:number):Observable<ScenarioModel> {
        return AxiosService.getInstance().get(`/api/scenaria/${id}`).pipe(map((response:any)=>response.data));;
    }

    createScenario(title:string, description?:string):Observable<ScenarioModel> {
        return AxiosService.getInstance().post<AxiosResponse>(`/api/scenaria`, { title, description}).pipe(map(response=>response.data));
    }

    updateScenario(id:number, title:string, description?:string):Observable<ScenarioModel> {
        return AxiosService.getInstance().put<AxiosResponse>(`/api/scenaria/${id}`, { title, description}).pipe(map(response=>response.data));
    }

    deleteScenario(id:number):Observable<void> {
        return AxiosService.getInstance().delete<AxiosResponse>(`/api/scenaria/${id}`).pipe(map(response=>response.data));
    }

    addPollutant(scenarioId:number, name:string, description?:string):Observable<PollutantModel> {
        return AxiosService.getInstance().post<AxiosResponse>(`/api/scenaria/${scenarioId}/pollutants`, { name, description}).pipe(map(response=>response.data));
    }

    updatePollutant(scenarioId:number, id:number, name:string, description?:string):Observable<PollutantModel> {
        return AxiosService.getInstance().put<AxiosResponse>(`/api/scenaria/${scenarioId}/pollutants/${id}`, { name, description }).pipe(map(response=>response.data));
    }

    deletePollutant(scenarioId:number, id:number):Observable<void> {
        return AxiosService.getInstance().delete<AxiosResponse>(`/api/scenaria/${scenarioId}/pollutants/${id}`).pipe(map(response=>response.data));
    }

    //name:string, min:number, max:number, description?: string, units?:string,
    addAnalysis(scenarioId:number, pollutantId:number, analysisAndFileData:FormData):Observable<AnalysisModel>{
        return AxiosService.getInstance().post<AxiosResponse>(`/api/scenaria/${scenarioId}/pollutants/${pollutantId}/analyses`, analysisAndFileData).pipe(map(response=>response.data));
    }

    updateAnalysis(scenarioId:number, pollutantId:number, id:number, analysisAndFileData:FormData):Observable<AnalysisModel> {
        return AxiosService.getInstance().post<AxiosResponse>(`/api/scenaria/${scenarioId}/pollutants/${pollutantId}/analyses/${id}?_method=PUT`, analysisAndFileData).pipe(map(response=>response.data));
    }

    deleteAnalysis(scenarioId:number, pollutantId:number, id:number):Observable<void> {
        return AxiosService.getInstance().delete<AxiosResponse>(`/api/scenaria/${scenarioId}/pollutants/${pollutantId}/analyses/${id}`).pipe(map(response=>response.data));
    }

    rebuildAnalysis(scenarioId:number, pollutantId:number, id:number):Observable<void> {
        return AxiosService.getInstance().put<AxiosResponse>(`/api/scenaria/${scenarioId}/pollutants/${pollutantId}/analyses/${id}/rebuild`, {}).pipe(map(response=>response.data));
    }

    getAnalysis(scenarioId:number, pollutantId:number, id:number):Observable<AnalysisModel> {
        return AxiosService.getInstance().get<AxiosResponse>(`/api/scenaria/${scenarioId}/pollutants/${pollutantId}/analyses/${id}`).pipe(map((response:any)=>{
            const model = response.analysis as AnalysisModel;
            model.process_dat_file_job = response.process_dat_file_job;
            model.generate_polygon_contours_batch = response.generate_polygon_contours_batch
            return model;

        }));
    }

    getAnalysisReceptorsData(scenarioId:number, pollutantId:number, id:number):Observable<AnalysisDataReceptorModel[]> {
        return AxiosService.getInstance().get<AxiosResponse>(`/api/scenaria/${scenarioId}/pollutants/${pollutantId}/analyses/${id}/receptors-data`).pipe(map((response:any)=>{
            const receptors = response as AnalysisDataReceptorModel[];
            return receptors;

        }));
    }

    getAnalysisContourLines(scenarioId:number, pollutantId:number, id:number, dateTime:dayjs.Dayjs|undefined):Observable<AnalysisContourLinesModel> {
        let dateQuery = "date_time=" + encodeURIComponent(dateTime ? dateTime.format('YYYY-MM-DD HH:mm') : '');
        return AxiosService.getInstance().get<AxiosResponse>(`/api/scenaria/${scenarioId}/pollutants/${pollutantId}/analyses/${id}/contour-lines?${dateQuery}`).pipe(map((response:any)=>{
            const contourLines = response as AnalysisContourLinesDailyModel[];

            return {dateTime:dateTime , data:contourLines};

        }));
    }

    getProcessingAnalysis():Observable<AnalysisModel[]> {
        return AxiosService.getInstance().get<AxiosResponse>(`/api/scenaria/processing-analyses`).pipe(map((response:any)=>{
            const analyses = response.data as AnalysisModel[];
            return analyses;

        }));
    }
}

export default ScenariaService;